import { Product } from '../types';
import { Locale } from '../i18n/locales';
import { SolutionsKm } from './solutions.km';

export const Solutions: Product[] = [
   {
      id: 1,
      slug: 'sales',
      name: 'Sales Solutions',
      shortName: 'Sales',
      tagline: 'Lead, quote, checkout, and collect in one chain',
      docsUrl: 'https://docs.akas.co/en/crm/',
      description:
         'Sell everywhere with connected POS, ecommerce, and CRM. Create quotes, convert to invoices, and track revenue from lead to cash with AKAS.',
      images: [],
      backgroundImage: '/images/backgrounds/crm.jpeg',
      mainFeatures: [
         {
            icon: '🧾',
            title: 'Quotations & Invoicing',
            description:
               'Build itemized quotations with taxes, discounts, and payment terms; convert to invoices in one click and collect payments faster.',
            images: [],
         },
         {
            icon: '🛒',
            title: 'Point of Sale (POS)',
            description:
               'Speed up checkout with barcode scanning, printable/emailed receipts, hold and resume carts, cash drawer reconciliation, and offline-first resilience.',
            images: [],
         },
         {
            icon: '🏪',
            title: 'Ecommerce & Catalog',
            description:
               'Publish product catalogs with pricing tiers, manage stock availability, and sync orders from web, marketplace, and in-store channels.',
            images: [],
         },
         {
            icon: '🔄',
            title: 'Sales Pipeline & CRM',
            description:
               'Capture leads from web forms, track opportunities, schedule follow-ups, and trigger automated handoffs to billing and fulfillment.',
            images: [],
         },
         {
            icon: '📦',
            title: 'Inventory & Fulfillment',
            description:
               'Track stock by location, reserve inventory on confirmed orders, and manage picking, packing, and shipping updates in one place.',
            images: [],
         },
         {
            icon: '📈',
            title: 'Sales Reporting',
            description:
               'Monitor revenue, margins, taxes, and channel performance with dashboards that mirror the KPIs documented in AKAS sales reports.',
            images: [],
         },
      ],
      meta: {
         title: 'AKAS | Sales solutions — quotes, POS, ecommerce and CRM',
         description:
            'Run quotations, invoices, POS, ecommerce, and CRM in one AKAS sales platform. Sell in-store and online with centralized products, inventory, and reporting.',
         keywords:
            'Sales Solution, POS, Ecommerce, CRM, Quotations, Invoicing, AKAS',
      },
      additionalFeatures: [
         {
            icon: '✓',
            title: 'Approval workflow',
            description:
               'Route discounts, credit notes, and large deals for approval before they post.',
         },
         {
            icon: '🌐',
            title: 'Multi-language & currency',
            description:
               'English and Khmer out of the box, with more languages, multi-currency pricing, and tax rules.',
         },
         {
            icon: '⚡',
            title: 'Offline-capable POS',
            description:
               'Keep selling during connectivity drops and sync when back online.',
         },
         {
            icon: '🔌',
            title: 'Integrations',
            description:
               'Connect payment gateways, barcode scanners, printers, and AKAS add-ons.',
         },
         {
            icon: '🔔',
            title: 'Notifications',
            description:
               'Alert sales, finance, and operations teams on key order and payment events.',
         },
         {
            icon: '📝',
            title: 'Audit logging',
            description:
               'Track user actions across quotations, invoices, and POS sessions.',
         },
      ],
      benefits: [
         {
            number: '01',
            title: 'Faster quote-to-cash',
            description:
               'Convert leads to invoices quickly with guided approvals and automated handoffs.',
         },
         {
            number: '02',
            title: 'Omnichannel control',
            description:
               'Keep POS, ecommerce, and B2B orders in sync with one product and inventory source.',
         },
         {
            number: '03',
            title: 'Reduced errors',
            description:
               'Standardize pricing, taxes, and stock reservations to cut rework and shrinkage.',
         },
         {
            number: '04',
            title: 'Better customer experience',
            description:
               'Give buyers consistent pricing, faster checkout, and clear order updates.',
         },
      ],
      videos: [],
      workflow: {
         title: 'Lead to cash',
         path: 'Lead → Customer → Quote → Sales order → Invoice → Receipt / POS / Store',
         steps: [
            {
               title: 'Win the deal',
               description: 'Capture leads, convert to customers, manage the pipeline, and send an approved quotation.',
            },
            {
               title: 'Confirm and bill',
               description: 'Turn the quote into a sales order, then invoice with the same items, taxes, and terms.',
            },
            {
               title: 'Sell in person or online',
               description: 'Mart and restaurant POS plus the online store use the same catalog and stock.',
            },
            {
               title: 'Collect and report',
               description: 'Receipts, KHQR, deposits, and AR query sit in Revenues and can post to Accounting.',
            },
         ],
      },
      highlights: [
         'Quotations convert to sales orders and invoices',
         'POS Mart and restaurant modes on the same catalog',
         'Online store, CRM pipeline, and revenue reports',
      ],
      related: [
         { label: 'CRM', to: '/products/crm' },
         { label: 'Revenues', to: '/products/revenues' },
         { label: 'POS', to: '/products/pos' },
         { label: 'Online store', to: '/products/online-store' },
      ],
   },
   {
      id: 2,
      slug: 'inventory',
      name: 'Inventory Solutions',
      shortName: 'Inventory',
      tagline: 'Order, receive, move, and balance stock across locations',
      docsUrl: 'https://docs.akas.co/en/inventory/',
      description:
         'Control stock across warehouses, keep counts accurate, and replenish on time with AKAS inventory.',
      images: [],
      backgroundImage: '/images/solutions/inventory-solution.jpg',
      mainFeatures: [
         {
            icon: '📦',
            title: 'Multi-location inventory',
            description:
               'Track on-hand, reserved, and available-to-promise by warehouse, store, or bin.',
            images: [],
         },
         {
            icon: '🔄',
            title: 'Stock movements',
            description:
               'Receive, transfer, and adjust stock with approval trails and variance notes.',
            images: [],
         },
         {
            icon: '🧾',
            title: 'Purchasing & replenishment',
            description:
               'Raise POs from reorder points and lead times; match receipts to supplier bills.',
            images: [],
         },
         {
            icon: '🚚',
            title: 'Fulfillment',
            description:
               'Pick, pack, and ship against sales orders with status updates to sales and finance.',
            images: [],
         },
         {
            icon: '🏷️',
            title: 'Lot / serial tracking',
            description:
               'Capture batch/serial numbers and expirations for traceability and recalls.',
            images: [],
         },
         {
            icon: '📈',
            title: 'Inventory analytics',
            description:
               'Monitor turnover, aging, valuation, and shrinkage with dashboards aligned to AKAS reports.',
            images: [],
         },
      ],
      meta: {
         title: 'AKAS | Inventory solutions — stock, purchasing and warehouses',
         description:
            'Control stock across warehouses, keep counts accurate, and replenish on time with AKAS inventory.',
         keywords:
            'Inventory Solution, Stock Control, Warehouse, Replenishment, AKAS',
      },
      additionalFeatures: [
         {
            icon: '✅',
            title: 'Cycle counts & audits',
            description:
               'Run scheduled counts and reconcile variances with approvals.',
         },
         {
            icon: '📡',
            title: 'Barcode & scanners',
            description:
               'Speed receiving and picking with barcode-friendly workflows.',
         },
         {
            icon: '🔔',
            title: 'Alerts',
            description:
               'Get notified on low stock, backorders, and delayed receipts.',
         },
         {
            icon: '🔌',
            title: 'Integrations',
            description:
               'Connect POS, ecommerce, procurement, and accounting modules.',
         },
         {
            icon: '📝',
            title: 'Audit logging',
            description: 'Capture who adjusted stock, when, and why.',
         },
      ],
      benefits: [
         {
            number: '01',
            title: 'Inventory accuracy',
            description:
               'Reduce stockouts and overstock with real-time, multi-location visibility.',
         },
         {
            number: '02',
            title: 'Faster fulfillment',
            description:
               'Pick, pack, and ship faster with guided workflows tied to sales orders.',
         },
         {
            number: '03',
            title: 'Lower carrying costs',
            description:
               'Replenish based on demand and lead times to cut excess stock.',
         },
         {
            number: '04',
            title: 'Compliance & traceability',
            description:
               'Lot/serial control and audit trails simplify recalls and inspections.',
         },
      ],
      videos: [],
      workflow: {
         title: 'Buy, hold, sell',
         path: 'Purchase order → Receive → Transfer / adjust / withdraw → Invoice deducts stock',
         steps: [
            {
               title: 'Order and receive',
               description: 'Raise purchase orders against vendors, then receive goods into a location.',
            },
            {
               title: 'Move stock',
               description: 'Transfer between warehouses, adjust after counts, or fulfill self-service withdrawals.',
            },
            {
               title: 'Sell',
               description: 'Invoicing inventory items in Revenues deducts on-hand quantity.',
            },
            {
               title: 'Verify',
               description: 'Balances and inventory reports show what is still on the shelf.',
            },
         ],
      },
      highlights: [
         'Purchase orders and goods received',
         'Transfers, adjustments, and self-service withdrawals',
         'Balances update when inventory items are invoiced',
      ],
      related: [
         { label: 'Inventory module', to: '/products/inventory' },
         { label: 'Expenses', to: '/products/expenses' },
         { label: 'Inventory user guide', href: 'https://docs.akas.co/en/inventory/' },
      ],
   },
   {
      id: 3,
      slug: 'hr',
      name: 'HR Solutions',
      shortName: 'HR',
      tagline: 'Hire, attend, pay, and develop people on one record',
      docsUrl: 'https://docs.akas.co/en/hrms/',
      description:
         'Run hire-to-retire on one employee record: recruitment, contracts, attendance, leave, payroll with local statutory reports, performance, training, and self-service — with approvals that match how your company works.',
      images: [],
      backgroundImage: '/images/solutions/hr-solution.jpg',
      mainFeatures: [
         {
            icon: '👥',
            title: 'Recruitment & onboarding',
            description:
               'Job requisitions become vacancies, applications flow to hire, and the new employee lands in Personnel with contract, salary, schedule, and leave quotas.',
            images: [],
         },
         {
            icon: '💻',
            title: 'Employee self-service',
            description:
               'Staff request leave, overtime, training, job requisitions, and resignation from the dashboard. Managers approve; employees see payslips and pending requests.',
            images: [],
         },
         {
            icon: '🕒',
            title: 'Attendance & leave',
            description:
               'Biometric punches, QR check-in, rosters, and timesheets feed leave balances and overtime — against holidays and approval rules you set.',
            images: [],
         },
         {
            icon: '💰',
            title: 'Payroll with statutory reports',
            description:
               'Pay elements, queues, and the payroll register calculate payslips plus salary tax (TOS01), NSSF eForm, seniority, fringe benefit, and bank transfer lists.',
            images: [],
         },
         {
            icon: '📈',
            title: 'Performance & training',
            description:
               'Run reviews and training requests on the same employee record so development is not a side spreadsheet.',
            images: [],
         },
         {
            icon: '✅',
            title: 'Approval workflows',
            description:
               'Route leave, OT, requisitions, and separations through your org chart — with temporary approval delegation when someone is away.',
            images: [],
         },
      ],
      meta: {
         title: 'AKAS | HR solutions — hire to retire, payroll and self-service',
         description:
            'AKAS HR solutions cover recruitment, attendance, leave, payroll with tax and NSSF reports, performance, and employee self-service on one record.',
         keywords:
            'HR Solution, HRMS, payroll, attendance, leave, NSSF, salary tax, AKAS',
      },
      additionalFeatures: [
         {
            icon: '🔐',
            title: 'Sensitive payroll access',
            description:
               'Role menus and extra sign-in protection keep salary data with the right people.',
         },
         {
            icon: '🌐',
            title: 'Khmer & English',
            description:
               'Screens, documents, and payslips in the languages your team actually uses.',
         },
         {
            icon: '🔔',
            title: 'Notifications',
            description:
               'Alert managers and staff on pending approvals, payslips, and key HR events.',
         },
         {
            icon: '📝',
            title: 'Audit trails',
            description:
               'Know who changed contracts, pay elements, and separations — and when.',
         },
      ],
      benefits: [
         {
            number: '01',
            title: 'One employee record',
            description:
               'Recruitment, attendance, leave, and payroll stop living in separate files.',
         },
         {
            number: '02',
            title: 'Payday without the scramble',
            description:
               'Statutory reports come out of the payroll run ready for finance to review.',
         },
         {
            number: '03',
            title: 'Self-service that sticks',
            description:
               'Employees raise requests themselves; managers approve on a clear queue.',
         },
         {
            number: '04',
            title: 'Rules you can change',
            description:
               'Approval paths and pay policies follow your org — not a fixed vendor template.',
         },
      ],
      videos: [],
      workflow: {
         title: 'Hire to retire',
         path: 'Requisition → Hire → Attendance & leave → Payroll → Review → Separation',
         steps: [
            {
               title: 'Recruit',
               description:
                  'Raise and approve job requisitions, open vacancies, collect applications, and hire into Personnel.',
            },
            {
               title: 'Employ',
               description:
                  'Create the person profile, employment contract, salary, leave quotas, work schedule, and reporting line.',
            },
            {
               title: 'Attend & leave',
               description:
                  'Capture punches and QR check-ins, process timesheets, and approve leave against balances and holidays.',
            },
            {
               title: 'Pay & develop',
               description:
                  'Run overtime and the payroll register, issue payslips and statutory reports, then performance and training.',
            },
         ],
      },
      highlights: [
         'Employee self-service for leave, OT, payslips, and resignation',
         'Payroll with salary tax, NSSF, seniority, and bank lists',
         'Attendance from devices, QR check-in, rosters, and timesheets',
      ],
      related: [
         { label: 'HRMS module', to: '/products/hrms' },
         { label: 'HRMS user guide', href: 'https://docs.akas.co/en/hrms/' },
         { label: 'Accounting', to: '/products/accounting' },
      ],
   },
   {
      id: 4,
      slug: 'accounting',
      name: 'Accounting Solutions',
      shortName: 'Accounting',
      tagline: 'Map operations to the ledger, then close the books',
      docsUrl: 'https://docs.akas.co/en/accounting/',
      description:
         'Structure the chart of accounts, map invoices and bills to GL accounts, and let approved sales and expenses post journal entries — then run income statement, balance sheet, cash flow, trial balance, and budget vs actual.',
      images: [],
      backgroundImage: '/images/solutions/accounting-solution.jpg',
      mainFeatures: [
         {
            icon: '📒',
            title: 'Chart of accounts & journals',
            description:
               'Build the account structure, journals, fiscal years, and budgets so posting and reporting follow one ledger.',
            images: [],
         },
         {
            icon: '🔗',
            title: 'GL mappings',
            description:
               'Tell AKAS which accounts invoices, bills, receipts, and payments should hit — so operations post instead of finance retyping.',
            images: [],
         },
         {
            icon: '✍️',
            title: 'Journal entries',
            description:
               'Review auto-posted entries from Revenues and Expenses, then add manual adjustments when you need a correcting journal.',
            images: [],
         },
         {
            icon: '📈',
            title: 'Financial statements',
            description:
               'Income statement, balance sheet, cash flow, and trial balance from the same ledger operational modules post into.',
            images: [],
         },
         {
            icon: '🎯',
            title: 'Budget vs actual',
            description:
               'Compare budgets to posted activity so departments see variance without a separate spreadsheet.',
            images: [],
         },
         {
            icon: '🔒',
            title: 'Fiscal controls',
            description:
               'Fiscal years and org units in Administration gate when posting is allowed — modules inherit the same calendar.',
            images: [],
         },
      ],
      meta: {
         title: 'AKAS | Accounting solutions — GL, statements and budgets',
         description:
            'AKAS Accounting maps operational documents to the general ledger and delivers income statement, balance sheet, cash flow, and budget vs actual.',
         keywords:
            'Accounting Solution, general ledger, income statement, balance sheet, GL mapping, AKAS',
      },
      additionalFeatures: [
         {
            icon: '✅',
            title: 'Role-based menus',
            description:
               'Accountants and reviewers see only the journals and statements their role allows.',
         },
         {
            icon: '🔔',
            title: 'Audit-ready history',
            description:
               'Document numbering and change history on sensitive records support reviews.',
         },
         {
            icon: '🔌',
            title: 'Tied to operations',
            description:
               'Revenues, Expenses, and Fixed Assets post through the mappings you define.',
         },
         {
            icon: '🌐',
            title: 'Multi-language',
            description:
               'Work the books in Khmer or English with the same account structure.',
         },
      ],
      benefits: [
         {
            number: '01',
            title: 'Books that follow the business',
            description:
               'A won invoice or paid bill posts the ledger — finance reviews instead of re-entering.',
         },
         {
            number: '02',
            title: 'Statements you trust',
            description:
               'P&L, balance sheet, and cash flow come from one chart of accounts.',
         },
         {
            number: '03',
            title: 'Budgets with teeth',
            description:
               'Budget vs actual shows variance against the same posted activity.',
         },
         {
            number: '04',
            title: 'Close with control',
            description:
               'Fiscal years and mappings keep periods clean and postings intentional.',
         },
      ],
      videos: [],
      workflow: {
         title: 'Close the books from operations',
         path: 'Chart of accounts → GL mappings → Operational posting → Statements',
         steps: [
            {
               title: 'Structure the ledger',
               description: 'Build the chart of accounts and define journals, fiscal years, and budgets.',
            },
            {
               title: 'Map transactions',
               description:
                  'Tell AKAS which GL accounts invoices, bills, receipts, and payments should hit.',
            },
            {
               title: 'Operate',
               description:
                  'Revenues and Expenses post journal entries when documents are approved.',
            },
            {
               title: 'Review and report',
               description:
                  'Inspect journal entries, add manual adjustments, then run financial statements and budget vs actual.',
            },
         ],
      },
      highlights: [
         'Chart of accounts, journals, and budgets',
         'GL mappings from invoices, bills, and payments',
         'Income statement, balance sheet, cash flow, trial balance',
      ],
      related: [
         { label: 'Accounting module', to: '/products/accounting' },
         { label: 'Revenues', to: '/products/revenues' },
         { label: 'Expenses', to: '/products/expenses' },
         { label: 'Accounting user guide', href: 'https://docs.akas.co/en/accounting/' },
      ],
   },
   {
      id: 5,
      slug: 'crm',
      name: 'CRM Solutions',
      shortName: 'CRM',
      tagline: 'Lead to quote on one customer account',
      docsUrl: 'https://docs.akas.co/en/crm/',
      description:
         'Capture leads, convert them to customers, manage opportunities on a pipeline, and send approved quotations that become sales orders — with the same customer record used by billing and receipts.',
      images: [],
      backgroundImage: '/images/solutions/crm-solution.jpg',
      mainFeatures: [
         {
            icon: '👤',
            title: 'Leads & customers',
            description:
               'Record prospects, convert to a customer account shared with Revenues, and keep notes and files where the next caller can find them.',
            images: [],
         },
         {
            icon: '📊',
            title: 'Pipeline & opportunities',
            description:
               'Track deal value, stage, and probability on a pipeline the team can manage — not a private spreadsheet.',
            images: [],
         },
         {
            icon: '🧾',
            title: 'Quotations',
            description:
               'Build itemized quotes with taxes, discounts, and terms. After approval, convert to a sales order without retyping lines.',
            images: [],
         },
         {
            icon: '🔄',
            title: 'Hand-off to billing',
            description:
               'Approved quotations continue in Revenues as sales orders, invoices, and receipts on the same customer.',
            images: [],
         },
         {
            icon: '📁',
            title: 'Sales history',
            description:
               'See orders and invoices for a customer in one place so follow-up uses real history.',
            images: [],
         },
         {
            icon: '📈',
            title: 'CRM reports',
            description:
               'Activity and conversion reports show what is moving and what is stuck.',
            images: [],
         },
      ],
      meta: {
         title: 'AKAS | CRM solutions — leads, pipeline and quotations',
         description:
            'AKAS CRM runs leads, customers, opportunities, and quotations that convert to sales orders — on one customer account shared with billing.',
         keywords:
            'CRM Solution, sales pipeline, quotations, leads, customer management, AKAS',
      },
      additionalFeatures: [
         {
            icon: '✅',
            title: 'Approval workflow',
            description:
               'Route large discounts and quotations for approval before they become orders.',
         },
         {
            icon: '🌐',
            title: 'Multi-language',
            description:
               'Work the pipeline in Khmer or English with shared customer master data.',
         },
         {
            icon: '🔔',
            title: 'Follow-up alerts',
            description:
               'Keep opportunities from going cold with reminders on key stages.',
         },
         {
            icon: '🔌',
            title: 'Connected modules',
            description:
               'Credit terms from Administration apply; Revenues, POS, and the store reuse the customer.',
         },
      ],
      benefits: [
         {
            number: '01',
            title: 'No double entry',
            description:
               'The customer won in CRM is the same account finance invoices.',
         },
         {
            number: '02',
            title: 'Pipeline you can run',
            description:
               'Stages, value, and probability live where the team already works.',
         },
         {
            number: '03',
            title: 'Faster quote-to-order',
            description:
               'Approved quotations convert without retyping line items.',
         },
         {
            number: '04',
            title: 'Context for every call',
            description:
               'Notes, files, and sales history travel with the customer record.',
         },
      ],
      videos: [],
      workflow: {
         title: 'Lead to quote',
         path: 'Leads → Customers → Opportunities → Quotations → Revenues',
         steps: [
            {
               title: 'Capture the lead',
               description: 'Record contact details and source so follow-up is not lost in inboxes.',
            },
            {
               title: 'Convert to a customer',
               description:
                  'Create the customer account used across CRM and Revenues, optionally with an opportunity.',
            },
            {
               title: 'Work the opportunity',
               description:
                  'Track value, stage, and probability on a pipeline the team can actually manage.',
            },
            {
               title: 'Quote, then bill',
               description:
                  'Send a formal quotation. After approval, convert it to a sales order in Revenues.',
            },
         ],
      },
      highlights: [
         'Leads convert to customers and opportunities',
         'Pipeline stages with deal value and probability',
         'Quotations approve and convert to sales orders',
      ],
      related: [
         { label: 'CRM module', to: '/products/crm' },
         { label: 'Revenues', to: '/products/revenues' },
         { label: 'Sales journey', to: '/solutions/sales' },
         { label: 'CRM user guide', href: 'https://docs.akas.co/en/crm/' },
      ],
   },
   {
      id: 6,
      slug: 'operations',
      name: 'Operations Solutions',
      shortName: 'Operations',
      tagline: 'Till, web shop, branches, and approvals on one org map',
      docsUrl: 'https://docs.akas.co/',
      description:
         'Run daily front-of-house and branch work from one place: Mart and restaurant POS, the online store, clinic appointments when you need them, and Administration rules for locations, roles, and approvals — so every channel sells the same items into the same stock and books.',
      images: [],
      backgroundImage: '/images/solutions/operations-solution.jpg',
      mainFeatures: [
         {
            icon: '🛒',
            title: 'Point of Sale',
            description:
               'Mart checkout and restaurant table service with kitchen printing — on Windows, macOS, and Android — using the same catalog and stock as Revenues and Inventory.',
            images: [],
         },
         {
            icon: '🏪',
            title: 'Online store',
            description:
               'Publish the same products and prices to the web. Orders sync back so warehouse and finance are not catching up from a side channel.',
            images: [],
         },
         {
            icon: '🏥',
            title: 'Clinic operations',
            description:
               'Appointments, patients, and dispensary inventory for clinics that need front desk and stock in the same suite.',
            images: [],
         },
         {
            icon: '🗺️',
            title: 'Branches & locations',
            description:
               'Set branches, warehouses, currencies, and the fiscal calendar once in Administration — every module obeys the same org map.',
            images: [],
         },
         {
            icon: '✅',
            title: 'Approval rules',
            description:
               'Draw the path for leave, quotes, bills, and stock. Change it as the business changes — no vendor rebuild.',
            images: [],
         },
         {
            icon: '🔐',
            title: 'Roles & access',
            description:
               'Every role gets its own menus. Sensitive steps like payroll stay behind extra sign-in protection.',
            images: [],
         },
      ],
      meta: {
         title: 'AKAS | Operations solutions — POS, store, branches and approvals',
         description:
            'AKAS Operations connects POS, the online store, clinic workflows, multi-location setup, and approval rules so every channel shares one catalog and org map.',
         keywords:
            'Operations Solution, POS, online store, multi-location, approvals, clinic, AKAS',
      },
      additionalFeatures: [
         {
            icon: '🌐',
            title: 'Khmer & English',
            description:
               'Screens and documents in the languages cashiers and managers actually use.',
         },
         {
            icon: '⚡',
            title: 'Offline-capable POS',
            description:
               'Keep selling during connectivity drops and sync when back online.',
         },
         {
            icon: '🔌',
            title: 'Shared catalog',
            description:
               'Items, prices, and stock are not re-entered for each channel.',
         },
         {
            icon: '📝',
            title: 'Audit trails',
            description:
               'Document numbering and change history support branch and till reviews.',
         },
      ],
      benefits: [
         {
            number: '01',
            title: 'One catalog everywhere',
            description:
               'A till sale and a web order deduct the same inventory item.',
         },
         {
            number: '02',
            title: 'Open a branch without a project',
            description:
               'Locations and roles are set once; modules inherit them.',
         },
         {
            number: '03',
            title: 'Rules that match how you work',
            description:
               'Approvals follow your org chart, not a fixed vendor template.',
         },
         {
            number: '04',
            title: 'Front desk to the books',
            description:
               'POS and store activity flows into revenue and stock — not a close-of-day spreadsheet.',
         },
      ],
      videos: [],
      workflow: {
         title: 'Channel to close',
         path: 'Set org & catalog → Sell (POS / store / clinic) → Stock & revenue update → Review',
         steps: [
            {
               title: 'Set the map',
               description:
                  'Define branches, warehouses, roles, currencies, and approval paths in Administration.',
            },
            {
               title: 'Publish once',
               description:
                  'Items and prices live in one catalog used by POS, the online store, and billing.',
            },
            {
               title: 'Sell every day',
               description:
                  'Cashiers, web customers, and clinic front desks work from the same system.',
            },
            {
               title: 'Reconcile without retyping',
               description:
                  'Stock moves and revenue records update so warehouse and finance see the same day.',
            },
         ],
      },
      highlights: [
         'POS Mart and restaurant modes on the shared catalog',
         'Online store orders sync to stock and revenue',
         'Branches, roles, and approvals set once for every module',
      ],
      related: [
         { label: 'POS', to: '/products/pos' },
         { label: 'Online store', to: '/products/online-store' },
         { label: 'Clinic', to: '/products/clinic' },
         { label: 'Sales journey', to: '/solutions/sales' },
      ],
   },
];

/** Overlay the Khmer records onto the English list, falling back per slug. */
function localizeSolutions(locale?: Locale): Product[] {
   if (locale !== 'km') return Solutions;
   const kmBySlug = new Map(SolutionsKm.map((solution) => [solution.slug, solution]));
   return Solutions.map((solution) => kmBySlug.get(solution.slug) ?? solution);
}

export function getSolutionBySlug(slug?: string, locale?: Locale): Product | undefined {
   if (!slug) return undefined;
   return localizeSolutions(locale).find((solution) => solution.slug === slug);
}
